import { useMemo, ChangeEvent } from "react";
import Button from "react-bootstrap/Button";
import Tooltip from "react-bootstrap/Tooltip";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import { resources } from "@constants/hardcoded-strings";
import { useModalContext } from "@context/use-modal-context";
import { LabelWidthTypes } from "@enums/label-width-types";
import { ModalTypes } from "@enums/modal-types";
import { SelectOption } from "@idw-types/common";
import { AxnCompany, AxnRelyingParty } from "@idw-types/data/reference-data/axn";
import { InputFormGroup } from "../../../common/input-form-group/input-form-group";
import { OrganizationSelectFormGroup, RelyingPartySelectFormGroup } from "../../../common/select-form-groups";
import { AddRelyingPartyModal } from "../../../relying-parties/add-relying-party-modal/add-relying-party-modal";
import styles from "../add-verification-workflow-modal.less";

type CommonWorkflowConfigurationSectionProps = {
    labelWidth: LabelWidthTypes;
    canAddNewRelyingParty: boolean;
    canAddRelyingPartyDueToLimits: boolean;
    axnCompanies: Array<AxnCompany>;
    availableRelyingParties: Array<AxnRelyingParty>;
    workflowName: string;
    selectedWorkflowOrganizationOption: SelectOption;
    selectedWorkflowRelyingPartyOption: SelectOption;
    workflowRedirectUrls: Array<string>;
    requestInProgress: boolean;
    onAddRelyingPartySuccess: (newAxnRelyingParty: AxnRelyingParty) => void;
    handleUpdateWorkflowName: (event: ChangeEvent<HTMLInputElement>) => void;
    handleUpdateWorkflowOrganization: (newOption: SelectOption) => void;
    handleUpdateWorkflowRelyingParty: (newOption: SelectOption) => void;
    handleUpdateWorkflowRedirectUrl: (event: ChangeEvent<HTMLInputElement>, index: number) => void;
    onClickAddRedirectUrl: () => void;
    removeWorkflowRedirectUrl: (index: number) => void;

};

export const CommonWorkflowConfigurationSection = (props: CommonWorkflowConfigurationSectionProps) => {
    const {
        labelWidth,
        canAddNewRelyingParty,
        canAddRelyingPartyDueToLimits,
        axnCompanies,
        availableRelyingParties,
        workflowName,
        selectedWorkflowOrganizationOption,
        selectedWorkflowRelyingPartyOption,
        workflowRedirectUrls,
        requestInProgress,
        onAddRelyingPartySuccess,
        handleUpdateWorkflowName,
        handleUpdateWorkflowOrganization,
        handleUpdateWorkflowRelyingParty,
        handleUpdateWorkflowRedirectUrl,
        removeWorkflowRedirectUrl,
        onClickAddRedirectUrl
    } = props;

    const modalContext = useModalContext();

    const createNewRelyingPartyButton = useMemo(() => {
        const renderAddRelyingPartyModal = (onModalClose: () => void) => {
            return (
                <AddRelyingPartyModal
                    onAddRelyingPartySuccess={onAddRelyingPartySuccess}
                    onModalClose={onModalClose}
                    axnCompanies={axnCompanies}
                />
            );
        };

        const onClickCreateNewRelyingParty = () => {
            modalContext.setModalState({
                modalType: ModalTypes.AddRelyingParty,
                renderFunction: renderAddRelyingPartyModal
            });
        };

        const actualButton = (
            <Button
                id="workflow-create-new-relying-party"
                onClick={onClickCreateNewRelyingParty}
                disabled={requestInProgress || !canAddRelyingPartyDueToLimits}
            >
                {resources.modals.addVerificationWorkflow.buttons.createNewRelyingParty.label}
            </Button>
        );

        if (canAddRelyingPartyDueToLimits) {
            return (
                <div className={styles.buttonDiv}>{actualButton}</div>
            );
        } else {
            const renderTooltip = (props: any) => {
                return (
                    <Tooltip {...props}>
                        {resources.pages.relyingParties.headerButtonDisabledText}
                    </Tooltip>
                );
            };

            return (
                <div className={styles.buttonDiv}>
                    <OverlayTrigger overlay={renderTooltip}>
                        {actualButton}
                    </OverlayTrigger>
                </div>
            );
        }
    }, [
        canAddNewRelyingParty,
        axnCompanies,
        canAddRelyingPartyDueToLimits,
        requestInProgress,
        onAddRelyingPartySuccess
    ]);

    return (
        <div>
            <InputFormGroup
                controlId="workflow-name-input"
                label={resources.modals.addVerificationWorkflow.inputs.name.label}
                ariaLabel={resources.modals.addVerificationWorkflow.inputs.name.label}
                automationId="workflow-name-input"
                placeholder={resources.modals.addVerificationWorkflow.inputs.name.placeholder}
                value={workflowName}
                onChange={handleUpdateWorkflowName}
                disabled={requestInProgress}
                isRequired={true}
                labelWidth={labelWidth}
            />
            <OrganizationSelectFormGroup
                idPrefix="workflow"
                onChange={handleUpdateWorkflowOrganization}
                axnCompanies={axnCompanies}
                selectedOption={selectedWorkflowOrganizationOption}
                disabled={requestInProgress || axnCompanies.length === 1}
                isRequired={true}
                isSearchable={true}
                isClearable={axnCompanies.length > 1}
                labelWidth={labelWidth}
            />
            <RelyingPartySelectFormGroup
                idPrefix="workflow"
                onChange={handleUpdateWorkflowRelyingParty}
                axnRelyingParties={availableRelyingParties}
                selectedOption={selectedWorkflowRelyingPartyOption}
                disabled={requestInProgress}
                isRequired={true}
                isSearchable={true}
                isClearable={false}
                labelWidth={labelWidth}
            />
            {canAddNewRelyingParty && (
                <div>{createNewRelyingPartyButton}</div>
            )}
            {workflowRedirectUrls.length > 0 && workflowRedirectUrls.map((url, index) => {
                return <InputFormGroup
                    key={`workflow-redirect-urls-input-${index}`}
                    controlId={`workflow-redirect-urls-input-${index}`}
                    label={index === 0 ? resources.modals.addVerificationWorkflow.inputs.redirectUrls.label : " "}
                    ariaLabel={index === 0 ? resources.modals.addVerificationWorkflow.inputs.redirectUrls.label : ""}
                    automationId={`workflow-redirect-urls-input-${index}`}
                    placeholder={resources.modals.addVerificationWorkflow.inputs.redirectUrls.placeholder}
                    value={url}
                    onChange={(event) => handleUpdateWorkflowRedirectUrl(event, index)}
                    disabled={requestInProgress}
                    isRequired={false}
                    labelWidth={labelWidth}
                    afterInputIcon={faTrash}
                    afterInputIconDisabled={requestInProgress}
                    afterInputIconOnClick={() => removeWorkflowRedirectUrl(index)}
                />;
            })}
            <div className={styles.buttonDiv}>
                {workflowRedirectUrls.length === 0 && (
                    <span>{resources.modals.addVerificationWorkflow.inputs.redirectUrls.label}</span>
                )}
                <Button id="workflow-add-redirect-url" onClick={onClickAddRedirectUrl} disabled={requestInProgress}>
                    {resources.modals.addVerificationWorkflow.buttons.addRedirectUrl.label}
                </Button>
            </div>
        </div>
    );
};